import { useRouter } from '@tanstack/react-router';
import type { ComponentProps, MouseEvent } from 'react';

import { Separator } from '@nucleus-commerce/ui/components/separator';
import { Table, TableCell } from '@nucleus-commerce/ui/components/table';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { Callout } from './callout';

/** In-app links go through the router so a guide never reloads the SPA; others open a new tab. */
export function MdxAnchor({ href = '', className, children, ...props }: ComponentProps<'a'>) {
  const router = useRouter();
  const classes = cn(
    'text-primary font-medium underline underline-offset-4 hover:no-underline',
    className,
  );
  if (href.startsWith('/')) {
    const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
      void router.navigate({ href });
    };
    return (
      <a href={href} onClick={onClick} className={classes} {...props}>
        {children}
      </a>
    );
  }
  if (href.startsWith('#')) {
    return (
      <a href={href} className={classes} {...props}>
        {children}
      </a>
    );
  }
  return (
    <a href={href} target="_blank" rel="noreferrer" className={classes} {...props}>
      {children}
    </a>
  );
}

export function MdxH2({ className, children, ...props }: ComponentProps<'h2'>) {
  return (
    <h2
      className={cn(
        'mt-12 scroll-m-20 border-b pb-2 text-2xl font-semibold tracking-tight first:mt-0',
        className,
      )}
      {...props}
    >
      {children}
    </h2>
  );
}

export function MdxH3({ className, children, ...props }: ComponentProps<'h3'>) {
  return (
    <h3
      className={cn('mt-8 scroll-m-20 text-lg font-semibold tracking-tight', className)}
      {...props}
    >
      {children}
    </h3>
  );
}

export function MdxH4({ className, children, ...props }: ComponentProps<'h4'>) {
  return (
    <h4 className={cn('mt-6 scroll-m-20 font-semibold', className)} {...props}>
      {children}
    </h4>
  );
}

export function MdxP({ className, ...props }: ComponentProps<'p'>) {
  return <p className={cn('leading-7 [&:not(:first-child)]:mt-5', className)} {...props} />;
}

export function MdxUl({ className, ...props }: ComponentProps<'ul'>) {
  return <ul className={cn('my-5 ml-6 list-disc [&>li]:mt-2', className)} {...props} />;
}

export function MdxOl({ className, ...props }: ComponentProps<'ol'>) {
  return <ol className={cn('my-5 ml-6 list-decimal [&>li]:mt-2', className)} {...props} />;
}

export function MdxBlockquote({ children }: ComponentProps<'blockquote'>) {
  return <Callout>{children}</Callout>;
}

export function MdxTable({ children }: ComponentProps<'table'>) {
  return (
    <div className="my-6 rounded-lg border">
      <Table>{children}</Table>
    </div>
  );
}

export function MdxTd({ className, ...props }: ComponentProps<'td'>) {
  return <TableCell className={cn('whitespace-normal', className)} {...props} />;
}

export function MdxCode({ className, ...props }: ComponentProps<'code'>) {
  return (
    <code
      className={cn('bg-muted rounded px-[0.3rem] py-[0.2rem] font-mono text-[0.85em]', className)}
      {...props}
    />
  );
}

export function MdxHr() {
  return <Separator className="my-10" />;
}
